import * as React from 'react'
import { useQuery } from 'react-apollo'

import { debounce, replace } from 'lodash'
import { useFormState } from 'react-use-form-state'
import OutsideClickHandler from 'react-outside-click-handler'
import { defineMessages, FormattedMessage, FormattedHTMLMessage, useIntl } from 'react-intl'

import createValidator from '@dooer/json-validator'
import createNormalizer from '@dooer/json-normalizer'

import { companyIsAB, pushGAEvent, commonKeyValues } from '../../lib/utils'

import Button from '../button'
import FormCard from '../form-card'
import AutoSuggest from '../autosuggest'
import { Text, getValidity } from '../form'

import { DooerComGetCompanies, DooerComGetCompaniesVariables } from './__generated__/DooerComGetCompanies'
import { getCompaniesData } from './get-companies'
import styles from './find-company.module.scss'
import notEmpty from '@dooer/not-empty/es'
import ApolloErrorMessage from '../apollo-error-message'
import Error from '../error'

const validateSvensktOrganisationsnummer = createValidator({
  type: 'string',
  format: 'svenskt-organisationsnummer',
})

const normalizeSvensktOrganisationsnummer = createNormalizer({
  type: 'string',
  format: 'svenskt-organisationsnummer',
})

const messages = defineMessages({
  errorOrganizationNumber: {
    defaultMessage: 'Hoppsan! Du måste ange ett korrekt organisationsnummer',
    id: 'component.find-company.error',
  },
  choose: {
    defaultMessage: 'Välj',
    id: 'component.find-company.choose',
  },
})

export interface IFindCompanyCompany {
  name: string
  orgNr: string
  displayOrgNr: string
  city: string
  companyCode: string
}

interface IFindCompanyProps {
  onSubmit: (company: IFindCompanyCompany) => void
  handleClickNotYetStarted?: () => void
  onlyShowInput?: boolean
  initialName?: string | null
  label?: string
  labelRight?: string | React.ReactNode
  hideIcon?: boolean
  disabled?: boolean
  required?: boolean
  placeholder?: string
  header?: React.ReactNode
}

interface ISuggestion {
  city: string
  co: string
  companyCode: string
  companyName: string
  orgNr: string
  displayOrgNr: string
  position: { latitude: string; longitude: string }
  street: string
  zipCode: string
}

interface IFormValues {
  organizationNumber?: string
}

const search = debounce((setValue: (v: string) => void, value: string) => setValue(value), 200, { trailing: true })

const _FindCompany: React.FC<IFindCompanyProps> = (props) => {
  const initialCompany = {
    name: props.initialName || '',
    displayOrgNr: '',
    orgNr: '',
    city: '',
    companyCode: '',
  }
  const [company, setCompany] = React.useState<IFindCompanyCompany>(initialCompany)
  const [val, setval] = React.useState<string>(props.initialName ?? '')

  return (
    <FindCompany
      {...props}
      onChange={(newVal: string | IFindCompanyCompany) => {
        setCompany({
          orgNr: '',
          displayOrgNr: '',
          city: '',
          companyCode: '',
          ...(typeof newVal === 'string' ? { name: newVal } : (newVal as IFindCompanyCompany)),
        })
        search(setval, typeof newVal === 'string' ? newVal : newVal.name)
      }}
      searchValue={val}
      value={company}
    />
  )
}

const FindCompany = ({
  onSubmit,
  handleClickNotYetStarted,
  onlyShowInput,
  label,
  labelRight,
  hideIcon,
  disabled,
  required,
  placeholder,
  header,
  value,
  onChange,
  searchValue,
}: IFindCompanyProps & {
  searchValue: string
  value: IFindCompanyCompany
  onChange: (val: string | IFindCompanyCompany) => void
}) => {
  const intl = useIntl()
  const { data, error } = useQuery<DooerComGetCompanies, DooerComGetCompaniesVariables>(getCompaniesData, {
    variables: {
      q: replace(searchValue, '-', ''),
    },
    skip: !searchValue,
  })

  const companySuggestions = (data?.publicCompanySearch ?? []).filter(notEmpty).map((e) => ({
    companyCode: e.legalgroupCode,
    companyName: e.name,
    orgNr: e.organizationNumber,
    displayOrgNr: e.displayOrganizationNumber,
    city: e.city,
  }))

  const errorMessages = {
    organizationNumber: intl.formatMessage(messages.errorOrganizationNumber),
  }

  const [highlighted, setHighlighted] = React.useState(0)
  const [showCompanySuggestions, setShowCompanySuggestions] = React.useState(true)
  const [formState, input] = useFormState<IFormValues>(
    {
      organizationNumber: '',
    },
    {
      withIds: true,
    }
  )

  const handleClickSuggestion = (suggestion: ISuggestion) => {
    const { companyName, orgNr, city, companyCode, displayOrgNr } = suggestion
    onChange({ name: '', orgNr: '', displayOrgNr: '', city: '', companyCode: '' })
    onSubmit({ name: companyName, orgNr, displayOrgNr, city, companyCode })

    if (suggestion.companyCode !== 'LimitedCompany') {
      pushGAEvent('sole-proprietorship-click-dropdown')
    }
    setHighlighted(0)
  }

  const handleChange = (name: string) => {
    onChange({ name, orgNr: '', displayOrgNr: '', city: '', companyCode: '' })
    setHighlighted(0)
  }

  const highlightOption = (direction: 'previous' | 'next') => {
    if (!companySuggestions || companySuggestions.length === 0) {
      return false
    }
    const totaltLength = companySuggestions.length - 1

    if (direction === 'next') {
      if (highlighted >= totaltLength) {
        setHighlighted(0)
      } else {
        setHighlighted(highlighted + 1)
      }
    } else if (highlighted === 0) {
      setHighlighted(totaltLength)
    } else {
      setHighlighted(highlighted - 1)
    }
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.keyCode) {
      case 40: // down:
        highlightOption('next')
        break
      case 38: // up:
        highlightOption('previous')
        break
      case 13: // enter
      case 9: // tab
        showCompanySuggestions &&
          companySuggestions[highlighted] &&
          handleClickSuggestion(companySuggestions[highlighted] as ISuggestion)
        break
      default:
    }
  }

  // Use for testing flow
  // React.useEffect(() => {
  //   handleClickSuggestion({
  //     city: 'Västerås',
  //     co: 'Spektred',
  //     companyCode: 'LimitedCompany',
  //     companyName: 'Spektred',
  //     orgNr: '556342-7391',
  //     position: { latitude: '15', longitude: '64' },
  //     street: 'Karlsgatan 10',
  //     zipCode: '73480',
  //   })
  // }, [handleClickSuggestion])

  return !onlyShowInput ? (
    <>
      <FormCard
        button={
          value.name.length > 2 && error ? (
            <Button
              block
              className={styles.submitButton}
              onClick={() => {
                if (formState.values.organizationNumber) {
                  onSubmit({
                    ...value,
                    orgNr: formState.values.organizationNumber,
                    city: '',
                    companyCode: companyIsAB(formState.values.organizationNumber) ? 'LimitedCompany' : '',
                  })
                }
              }}
              variant="success"
            >
              <FormattedMessage defaultMessage="Gå vidare" id="signup-v3.step-1.hero.alt-button" />
            </Button>
          ) : null
        }
        extra={
          <div className={styles.linkButtonWrapper}>
            <Button onClick={() => handleClickNotYetStarted && handleClickNotYetStarted()} variant="link">
              <FormattedMessage
                defaultMessage="Mitt bolag har inte registrerats än"
                id="signup-v3.step1.hero.link-cta1"
              />
            </Button>
          </div>
        }
        header={
          header || (
            <FormattedHTMLMessage
              defaultMessage="Ange ditt företagsnamn och få ett pris på 2 minuter"
              id="signup.step1.hero.findCompany.label"
            />
          )
        }
        inputs={[
          {
            node: (
              <OutsideClickHandler key={1} onOutsideClick={() => setShowCompanySuggestions(false)}>
                <AutoSuggest
                  disabled={disabled}
                  hideIcon={hideIcon}
                  highlighted={highlighted}
                  icon="search"
                  label={label}
                  labelRight={labelRight}
                  name="companyName"
                  onChange={(e) => handleChange(e.target.value)}
                  onFocus={() => setShowCompanySuggestions(true)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  renderSuggestion={(suggestion, i) => (
                    <SuggestionItem
                      handleClickSuggestion={(s: ISuggestion) => handleClickSuggestion(s)}
                      isActive={i === highlighted}
                      suggestion={suggestion}
                    />
                  )}
                  required={required}
                  suggestions={showCompanySuggestions ? companySuggestions : []}
                  value={value.name}
                />
              </OutsideClickHandler>
            ),
            key: 1,
          },
          ...(value.name.length > 2 && error
            ? [
                {
                  node: (
                    <Text
                      errorMessage={errorMessages.organizationNumber}
                      hideIcon
                      input={input}
                      key={3}
                      name="organizationNumber"
                      pattern="\d*"
                      placeholder="Organisationsnummer"
                      required={required}
                      valid={getValidity(formState, 'organizationNumber')}
                      validate={(newValue: string) =>
                        validateSvensktOrganisationsnummer.isValid(normalizeSvensktOrganisationsnummer(newValue))
                          ? true
                          : errorMessages.organizationNumber
                      }
                    />
                  ),
                  key: 2,
                },
              ]
            : []),
        ]}
      />
    </>
  ) : (
    <>
      <OutsideClickHandler key={1} onOutsideClick={() => setShowCompanySuggestions(false)}>
        <AutoSuggest
          disabled={disabled}
          hideIcon={hideIcon}
          highlighted={highlighted}
          icon="search"
          label={label}
          labelRight={labelRight}
          name="companyName"
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setShowCompanySuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          renderSuggestion={(suggestion, i) => (
            <SuggestionItem
              handleClickSuggestion={(s: ISuggestion) => handleClickSuggestion(s)}
              isActive={i === highlighted}
              suggestion={suggestion}
            />
          )}
          required={required}
          suggestions={showCompanySuggestions ? companySuggestions : []}
          value={value.name}
        />
      </OutsideClickHandler>
      {error ? (
        <Error>
          <ApolloErrorMessage error={error} />
        </Error>
      ) : null}
    </>
  )
}

function SuggestionItem({
  handleClickSuggestion,
  suggestion,
  isActive,
}: {
  handleClickSuggestion: (suggestion: ISuggestion) => void
  suggestion: ISuggestion
  isActive?: boolean
}) {
  const [isHovering, setIsHovering] = React.useState<boolean>(false)
  return (
    <div
      className={[styles.suggestion, isActive && styles.active].filter(Boolean).join(' ')}
      onBlur={() => setIsHovering(false)}
      onClick={() => handleClickSuggestion(suggestion)}
      onFocus={() => setIsHovering(true)}
      onKeyDown={(e) => e.keyCode === commonKeyValues.enter && handleClickSuggestion(suggestion)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseOver={() => setIsHovering(true)}
      role="button"
      tabIndex={0}
    >
      <div className={styles.left}>
        <div className={styles.leftTop}>
          <span>{suggestion.companyName}</span>
        </div>

        <div className={styles.leftBottom}>
          <span>{suggestion.city ? `${suggestion.city}, ` : ''}</span>
          <span>{suggestion.displayOrgNr}</span>
        </div>
      </div>
      <div className={styles.right}>
        <Button disabled={!isActive && !isHovering} tabIndex={-1} variant={isActive || isHovering ? 'success' : 'gray'}>
          <FormattedMessage {...messages.choose} />
        </Button>
      </div>
    </div>
  )
}

export default _FindCompany
